<template>
  <div>
    <b-card body-class="pb-2 scan-form-card">
      <ValidationObserver
        v-slot="{ handleSubmit }"
        slim
      >
        <b-form
          class="mb-0 scan-form"
          @submit.prevent="handleSubmit(onSubmit)"
          @keydown.enter.prevent
        >
          <b-row class="align-items-start scan-form__top-row">
            <b-col
              cols="12"
              :md="sortingModuleEnabled ? 9 : 12"
              class="scan-form__left-col"
            >
              <b-row class="align-items-end scan-form__fields-row">
                <b-col
                  cols="12"
                  md="3"
                  class="scan-form__waybill-col"
                >
                  <b-form-group
                    label="Waybill Number"
                    label-for="waybill"
                    class="mb-md-0"
                  >
                    <b-form-input
                      id="waybill"
                      ref="waybill"
                      v-model="form.waybill_number"
                      type="text"
                      autocomplete="off"
                      :disabled="loadingResponse"
                      @paste="handlePaste"
                      @keydown="handleKeyDown"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="auto"
                  class="scan-form__search-col"
                >
                  <WaybillSearch
                    :waybill-number="form.waybill_number"
                    @search="getOrderDetails(form.waybill_number)"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="scan-form__metric-col"
                >
                  <b-form-group
                    label="Weight (KG)"
                    label-for="weight"
                    class="mb-md-0"
                  >
                    <b-form-input
                      id="weight"
                      :key="formKey"
                      ref="weightInput"
                      v-model="form.weight"
                      type="number"
                      placeholder="1"
                      autocomplete="off"
                      step=".01"
                      pattern="^\d*(\.\d{0,2})?$"
                      @input="checkWeightRange"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  v-if="isAutoSubmit"
                  cols="6"
                  md="2"
                  class="scan-form__metric-col"
                >
                  <b-form-group
                    label="Freeze Weight (KG)"
                    label-for="freeze_weight"
                    class="mb-md-0"
                  >
                    <b-form-input
                      id="freeze_weight"
                      v-model="freezeWeight"
                      type="number"
                      step=".01"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="12"
                  :md="isAutoSubmit ? 3 : 4"
                  class="scan-form__dest-col"
                >
                  <b-form-group
                    label="Destination Warehouse"
                    label-for="destination_warehouse"
                    class="mb-md-0"
                  >
                    <b-form-input
                      id="destination_warehouse"
                      :key="formKey"
                      v-model="form.destination_warehouse"
                      type="text"
                      :disabled="true"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row class="mt-1 align-items-center scan-form__checkbox-row">
                <b-col cols="auto">
                  <b-form-checkbox
                    id="auto-submit"
                    v-model="isAutoSubmit"
                    class="mb-0"
                    name="auto-submit"
                    :value="true"
                    :unchecked-value="false"
                  >
                    Auto Submit
                  </b-form-checkbox>
                </b-col>
                <b-col
                  v-if="volumetricSettings.enabled"
                  cols="auto"
                >
                  <b-form-checkbox
                    id="calculate-volumetric-weight"
                    v-model="calculateVolumetricWeight"
                    class="mb-0"
                    name="calculate-volumetric-weight"
                    :value="true"
                    :unchecked-value="false"
                  >
                    Calculate Volumetric Weight
                  </b-form-checkbox>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              v-if="sortingModuleEnabled"
              cols="12"
              md="3"
              class="scan-form__right-col"
            >
              <div
                class="scanned-order scanned-order--panel"
                :class="{ 'scanned-order--empty': !current_loc }"
                :style="{
                  '--bucket-bg-color': bucketColor ? bucketColor : 'rgba(115, 103, 240, 0.2)'
                }"
              >
                {{ current_loc || '-' }}
              </div>
            </b-col>
          </b-row>

          <b-row
            v-if="volumetricSettings.enabled && calculateVolumetricWeight"
            class="mt-2 align-items-stretch scan-form__volumetric-row"
          >
            <b-col
              cols="12"
              md="12"
              class="scan-form__volumetric-col"
            >
              <div class="volumetric-section">
                <b-row class="volumetric-section__body align-items-start">
                  <b-col
                    cols="12"
                    md="9"
                    class="volumetric-section__dimensions scan-form__left-col"
                  >
                    <div class="volumetric-section__title">
                      Package Dimensions
                    </div>
                    <b-row class="volumetric-section__labels-row flex-nowrap">
                      <b-col class="volumetric-field-col volumetric-field-col--dimension">
                        <label
                          for="length"
                          class="volumetric-field-label"
                        >Length</label>
                      </b-col>
                      <b-col
                        cols="auto"
                        class="volumetric-separator-spacer d-none d-md-block"
                      />
                      <b-col class="volumetric-field-col volumetric-field-col--dimension">
                        <label
                          for="width"
                          class="volumetric-field-label"
                        >Width</label>
                      </b-col>
                      <b-col
                        cols="auto"
                        class="volumetric-separator-spacer d-none d-md-block"
                      />
                      <b-col class="volumetric-field-col volumetric-field-col--dimension">
                        <label
                          for="height"
                          class="volumetric-field-label"
                        >Height</label>
                      </b-col>
                      <b-col
                        cols="auto"
                        class="volumetric-separator-spacer d-none d-md-block"
                      />
                      <b-col class="volumetric-field-col volumetric-field-col--weight">
                        <label
                          for="volumetric_weight"
                          class="volumetric-field-label"
                        >Volumetric Weight</label>
                      </b-col>
                    </b-row>
                    <b-row class="volumetric-section__equation-row align-items-center flex-nowrap">
                      <b-col class="volumetric-field-col volumetric-field-col--dimension">
                        <b-input-group class="volumetric-input-group">
                          <b-form-input
                            id="length"
                            v-model="form.volumetric_dimension.length"
                            type="number"
                            autocomplete="nope"
                            step=".01"
                            pattern="^\d*(\.\d{0,2})?$"
                            @keydown="preventNegative($event)"
                          />
                          <b-input-group-append is-text>
                            cm
                          </b-input-group-append>
                        </b-input-group>
                      </b-col>
                      <b-col
                        cols="auto"
                        class="volumetric-separator-col"
                      >
                        <span class="volumetric-separator">x</span>
                      </b-col>
                      <b-col class="volumetric-field-col volumetric-field-col--dimension">
                        <b-input-group class="volumetric-input-group">
                          <b-form-input
                            id="width"
                            v-model="form.volumetric_dimension.width"
                            type="number"
                            autocomplete="nope"
                            step=".01"
                            pattern="^\d*(\.\d{0,2})?$"
                            @keydown="preventNegative($event)"
                          />
                          <b-input-group-append is-text>
                            cm
                          </b-input-group-append>
                        </b-input-group>
                      </b-col>
                      <b-col
                        cols="auto"
                        class="volumetric-separator-col"
                      >
                        <span class="volumetric-separator">x</span>
                      </b-col>
                      <b-col class="volumetric-field-col volumetric-field-col--dimension">
                        <b-input-group class="volumetric-input-group">
                          <b-form-input
                            id="height"
                            v-model="form.volumetric_dimension.height"
                            type="number"
                            autocomplete="nope"
                            step=".01"
                            pattern="^\d*(\.\d{0,2})?$"
                            @keydown="preventNegative($event)"
                          />
                          <b-input-group-append is-text>
                            cm
                          </b-input-group-append>
                        </b-input-group>
                      </b-col>
                      <b-col
                        cols="auto"
                        class="volumetric-separator-col"
                      >
                        <span class="volumetric-separator">=</span>
                      </b-col>
                      <b-col class="volumetric-field-col volumetric-field-col--weight">
                        <b-input-group class="volumetric-input-group volumetric-input-group--weight">
                          <b-form-input
                            id="volumetric_weight"
                            v-model="form.volumetric_weight"
                            class="volumetric-weight-input"
                            readonly
                          />
                          <b-input-group-append class="volumetric-weight-append">
                            <span class="volumetric-weight-badge">kg(V)</span>
                          </b-input-group-append>
                        </b-input-group>
                      </b-col>
                    </b-row>
                  </b-col>
                  <b-col
                    cols="12"
                    md="3"
                    class="volumetric-section__summary scan-form__right-col"
                  >
                    <div class="weight-summary weight-summary--in-card">
                      <div class="weight-summary__title">
                        Weight Summary
                      </div>
                      <div class="weight-summary__row">
                        <span class="weight-summary__label">Actual Weight</span>
                        <span class="weight-summary__value">{{ summaryActualWeight }} kg</span>
                      </div>
                      <div class="weight-summary__row">
                        <span class="weight-summary__label">Volumetric Weight</span>
                        <span class="weight-summary__value">{{ summaryVolumetricWeight }} kg</span>
                      </div>
                      <div class="weight-summary__divider" />
                      <div class="weight-summary__row weight-summary__row--chargeable">
                        <span class="weight-summary__label">Chargeable Weight</span>
                        <span class="weight-summary__value weight-summary__value--chargeable">{{ summaryChargeableWeight }} kg</span>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </div>
            </b-col>
          </b-row>

          <b-row class="mt-2 scan-form__submit-row">
            <b-col
              cols="12"
              :md="sortingModuleEnabled ? 9 : 12"
            />
            <b-col
              v-if="sortingModuleEnabled"
              cols="12"
              md="3"
              class="scan-form__right-col scan-form__submit-col"
            >
              <b-button
                type="submit"
                class="vh-5 custom-button-color"
                :disabled="!(form.weight && form.waybill_number) || loadingResponse"
              >
                {{ loadingResponse ? 'Submitting...' : 'Submit' }}
              </b-button>
            </b-col>
            <b-col
              v-else
              cols="12"
              class="d-flex justify-content-end"
            >
              <b-button
                type="submit"
                class="vh-5 custom-button-color"
                :disabled="!(form.weight && form.waybill_number) || loadingResponse"
              >
                {{ loadingResponse ? 'Submitting...' : 'Submit' }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </ValidationObserver>
    </b-card>
    <FilterCard :applied-filter-count="appliedFilterCount">
      <b-row>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Created Date"
            label-for="basicInput"
          >
            <div class="position-relative">
              <AdvancedDateFilter
                :date-value="filters['orders.created_at']"
                :opens="'right'"
                @update="updateDateRange('orders.created_at', $event)"
              />
            </div>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Merchant"
            label-for="merchant"
          >
            <v-select
              v-model="filters['merchant_id']"
              :options="merchantOptions"
              :reduce="option => option.id"
              label="name"
              placeholder="Select Merchant"
            >
              <template v-slot:option="option">
                {{ option.name }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
      </b-row>
    </FilterCard>
    <b-card no-body>
      <div class="m-2">
        <b-row cols="12">
          <b-col
            md="12"
            lg="4"
            class="d-block d-sm-flex flex-wrap justify-content-start align-items-center mb-1 mb-md-0"
          >
            <bulk-submit
              :status-key="status_key"
              :selected-waybills="selectedWaybillArray"
              @bulkSubmit="onClickBulkAction"
            />
          </b-col>
          <b-col
            class="d-flex align-items-center justify-content-center mb-1 mb-md-0"
          >
            <b-button
              type="submit"
              class="custom-button-color-blur"
              :disabled="false"
            >
              Scanned Orders : {{ submitCount }}
            </b-button>
          </b-col>
          <b-col
            class="d-flex justify-content-end align-items-center mb-1 mb-md-0"
          >
            <ReportTableButtons
              :items="rows"
              :json_fieldz="json_fields"
              :name="`RecievedToOriginWarehouse - Page ${page}.xls`"
              :bulkname="`RecievedToOriginWarehouse.xls`"
              :fetch="getOrderListNoPagination"
              @refresh="onClickRefresh"
            />
          </b-col>
        </b-row>
        <b-row>
          <div class="font-weight-bolder text-muted ml-1 mt-1">
            {{ `${selectedWaybillArray.length} record${selectedWaybillArray.length === 1 ? '':'s'}` }} selected
          </div>
        </b-row>
      </div>

      <b-row>
        <b-col>
          <b-overlay
            id="overlay-background"
            blur="10px"
            opacity="0.30"
            rounded="sm"
            variant="light"
          >
            <b-table
              striped
              hover
              responsive
              :per-page="perPage"
              :items="rows"
              :fields="fields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :sort-direction="sortDirection"
              show-empty
              sticky-header="100vh"
              class="hide-vertical-scroll"
              @sort-changed="sortChanged"
            >
              <template #empty>
                <TableDataFetching
                  :rows="rows"
                  :data-loading="dataLoading"
                />
              </template>
              <template #head()="{ field }">
                <div
                  class="sortable-header"
                >
                  <span class="header-text">{{ field.label }}</span>
                  <feather-icon
                    v-if="field.sortable"
                    :icon="sort === field.key
                      ? 'ArrowUpIcon'
                      : 'ArrowDownIcon'"
                    size="14"
                  />
                </div>
              </template>
              <template #head(id)>
                <div class="d-flex align-items-start">
                  <div>
                    <b-form-checkbox
                      id="selectAllCheckbox"
                      v-model="selectAllCheckbox"
                      name="selectAllCheckbox"
                      @change="onClickSelectAllCheckbox()"
                    />
                  </div>
                </div>
              </template>
              <template #cell(id)="data">
                <div class="d-flex align-items-start">
                  <div>
                    <div class="font-weight-bolder">
                      <b-form-checkbox
                        v-model="selectedWaybillArray"
                        :value="data.item.waybill_number"
                      />
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(waybill_number)="data">
                <div class="d-flex align-items-center">
                  <b-avatar
                    :text="avatarText(data.item.customer_name)"
                    size="32"
                    variant="light-primary"
                  />
                  <div>
                    <div
                      :class="{'font-weight-bolder ml-1 text-danger': data.item.temporary_warehouse_id !=null ,'font-weight-bolder ml-1 text-primary':data.item.temporary_warehouse_id ===null}"
                      @click="onClickViewOrderModal(data.item.id)"
                    >
                      {{ data.value }}
                    </div>
                    <div class="font-small-2 ml-1">
                      {{ __dateTimeFormatter(data.item.created_at) }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(merchant.name)="data">
                <div class="d-flex align-items-start">
                  <div>
                    <div class="">
                      {{ __orderMerchantName(data.item) }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(customer_email)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder">
                      {{ data.item.customer_name }}
                    </div>
                    <div class="font-small-2">
                      {{ data.value }}
                    </div>
                    <div class="font-small-2">
                      <div><b>Primary number: </b>{{ data.item.customer_phone }}</div>
                    </div>
                    <div
                      v-if="data.item.customer_secondary_phone !== null"
                      class="font-small-2"
                    >
                      <div><b>Second number: </b>{{ data.item.customer_secondary_phone }}</div>
                    </div>
                    <div class="font-small-2">
                      {{ data.item.customer_address }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(origin_warehouse)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder">
                      CITY - {{ data.item.origin_city.name }}
                    </div>
                    <div class="font-small-2">
                      Warehouse - {{ data.value.name || 'N/A' }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(destination_warehouse)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder">
                      CITY - {{ data.item.destination_city.name }}
                      <!--                    </b-badge>-->
                    </div>
                    <div class="font-small-2">
                      Warehouse - {{ data.value.name || 'N/A' }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(finance)="data">
                <div
                  v-if="__getMerchantType(data.item) !== 'WC'"
                  class="width-150"
                >
                  <b>Total&nbsp;:</b>&nbsp;&nbsp;{{ __numberWithCommas(data.item.cod, true) || '---' }}
                  <div style="height:8px;" />
                  <b>Collected&nbsp;:</b>&nbsp;&nbsp;{{ data.item.collected_cod || '---' }}
                  <div style="height:8px;" />
                  <b>D-Charge&nbsp;:</b>&nbsp;&nbsp;{{ data.item.delivery_charge || '---' }}
                </div>
                <div
                  v-else
                  class="width-150"
                >
                  <b>D-Charge&nbsp;:</b>&nbsp;&nbsp;{{ data.item.delivery_charge || '---' }}
                  <div style="height:8px;" />
                  <b>Advance&nbsp;:</b>&nbsp;&nbsp;{{ data.item.collected_cod || '---' }}
                  <div style="height:8px;" />
                  <b>Balance&nbsp;:</b>&nbsp;&nbsp;{{ (data.item.delivery_charge - data.item.collected_cod ) || '---' }}
                </div>
              </template>
              <template #cell(created_at)="data">
                {{ __dateTimeFormatter(data.value) }}
              </template>
              <template #cell(status.key)="data">
                <order-status-badge :status-key="data.value" />
              </template>
              <template #cell(action)="data">
                <b-dropdown
                  variant="link"
                  toggle-class="text-decoration-none"
                  no-caret
                >
                  <template v-slot:button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="12"
                      class="text-body align-middle mr-25"
                    />
                  </template>
                  <b-dropdown-item
                    @click="onClickViewOrderModal(data.item.id)"
                  >
                    <feather-icon icon="EyeIcon" />
                    View
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
          </b-overlay>

          <div class="mx-2 mb-2">
            <b-row
              cols="12"
            >
              <b-col
                class="d-flex align-items-center justify-content-sm-start"
                md="3"
              >
                <label class="width-75">Per page</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="pageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block width-100"
                />
              </b-col>
              <!-- Pagination -->
              <b-col
                class="d-flex align-items-center justify-content-sm-end"
                md="9"
              >
                <span class="text-muted mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
                <b-pagination
                  v-model="page"
                  :per-page="perPage"
                  :total-rows="(meta.total)? meta.total : 0"
                  class="mb-0 mt-1 mt-sm-0"
                  first-number
                  last-number
                  next-class="next-item"
                  prev-class="prev-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </b-card>
    <bulk-action-modal
      ref="bulkActionModal"
      :form_structure="formStructure"
      :status_key="status_key"
      :selected_waybill_array="selectedWaybillArray"
      :order_settings="orderSettings"
    />
    <view-order-modal ref="viewOrderModal" />
  </div>
</template>

<script>
import {
  BAvatar,
  BRow,
  BCol,
  BTable,
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BForm,
  BPagination,
  BOverlay,
  BDropdown,
  BDropdownItem,
  BFormCheckbox,
  BInputGroup,
  BInputGroupAppend,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import OrderStatusBadge from '@core/components/status/OrderStatusBadge.vue'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Filter from '@core/mixins/filter'
import { avatarText } from '@core/utils/filter'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import store from '@/store'
// import BInputLabelInfo from '@/@core/components/b-input-label-info/BInputLabelInfo.vue'
import ViewOrderModal from '@/components/AllOrders/ViewOrderModal.vue'
import tableMixin from '@core/mixins/tableMixin'
import FilterCard from '@/components/FilterCard/FilterCard.vue'
import BulkActionModal from './bulk-actions/BulkActionModal.vue'
import AutoFetch from './AutoFetch'
import WaybillSearch from './WaybillSearch/WaybillSearch.vue'
import BulkSubmit from './bulk-actions/BulkSubmit.vue'

const OrderSettingsRepository = RepositoryFactory.get('orderSetting')
const orderRepository = RepositoryFactory.get('order')
const merchantRepository = RepositoryFactory.get('merchant')
const sortingRepository = RepositoryFactory.get('sorting')
const ResourceRepository = RepositoryFactory.get('resource')
const VolumetricSettingsRepository = RepositoryFactory.get('volumetricWeight')

export default {
  name: 'KeyThirtyFive',
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    AdvancedDateFilter,
    BAvatar,
    BRow,
    BCol,
    BTable,
    BCard,
    FilterCard,
    BFormInput,
    BForm,
    BButton,
    BFormGroup,
    BPagination,
    BOverlay,
    OrderStatusBadge,
    BDropdown,
    BDropdownItem,
    BulkActionModal,
    BFormCheckbox,
    BInputGroup,
    BInputGroupAppend,
    ViewOrderModal,
    vSelect,
    // BInputLabelInfo,
    WaybillSearch,
    BulkSubmit,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, AutoFetch, tableMixin],
  props: {
    // eslint-disable-next-line vue/prop-name-casing
    status_key: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isAutoSubmit: false,
      calculateVolumetricWeight: false,
      sortingModuleEnabled: false,
      freezeWeight: null,
      volumetricSettings: {},
      loadingResponse: false,
      submitCount: 0,
      filterAuto: true,
      // Table Info
      avatarText,
      filters: {
        'orders.created_at': {
          startDate: null,
          endDate: null,
        },
      },
      total: 0,
      meta: {},
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50],
      totalRows: 1,
      page: 1,
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filterName: '',
      bucketColor: 'white',
      fields: [
        {
          key: 'id',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'waybill_number',
          label: 'WayBill Number',
          sortable: true,
        },
        {
          key: 'merchant.name',
          label: 'Merchant',
          sortable: true,
        },
        {
          key: 'customer_name',
          label: 'Customer Name',
          sortable: true,
          thClass: 'd-none',
          tdClass: 'd-none',
        },
        {
          key: 'origin_warehouse',
          label: 'Origin',
          sortable: true,
        },
        {
          key: 'destination_warehouse',
          label: 'Destination',
          sortable: true,
        },
        {
          key: 'customer_email',
          label: 'Customer',
          sortable: true,
        },
        {
          key: 'finance',
          label: 'Finance',
          sortable: false,
        },
        {
          key: 'weight',
          label: 'Net Weight',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: true,
        },
        {
          key: 'status.key',
          label: 'Status',
          sortable: true,
        },
        {
          key: 'action',
          label: 'Action',
          sortable: false,
        },
      ],
      json_fields: {
        'Waybill Number': 'waybill_number',
        Merchant: {
          field: 'merchant.name',
          callback: value => `"${value}"`,
        },
        'Origin City': {
          field: 'origin_city.name',
          callback: value => `"${value}"`,
        },
        'Origin Warehouse': {
          field: 'origin_warehouse.name',
          callback: value => `"${value}"`,
        },
        'Destination City': {
          field: 'destination_city.name',
          callback: value => `"${value}"`,
        },
        'Destination Warehouse': {
          field: 'destination_warehouse.name',
          callback: value => `"${value}"`,
        },
        'Customer Name': {
          field: 'customer_name',
          callback: value => `"${value}"`,
        },
        'Customer Email': {
          field: 'customer_email',
          callback: value => `"${value}"`,
        },
        'Customer Address': {
          field: 'customer_address',
          callback: value => `"${value}"`, // Wrap the address in double quotes
        },
        'Customer Phone': 'customer_phone',
        'Second Phone Number': 'customer_secondary_phone',
        COD: 'cod',
        'Collected Cod': 'collected_cod',
        'Delivery Charge': 'delivery_charge',
        'Net Weight': 'weight',
        Status: 'status.name',
      },
      rows: [],
      // Form Info
      form: {},
      formStructure: [
        {
          type: 'input',
          placeholder: '',
          inputType: 'text',
          title: 'Waybill Number',
          value: 'waybill_number',
          class: 'col-6 col-md-3',
          validation: 'required',
        },
        {
          type: 'input',
          placeholder: '',
          inputType: 'number',
          title: 'Weight (Kg)',
          value: 'weight',
          class: 'col-6 col-md-3',
        },
      ],
      selectedWaybillArray: [],
      waybillData: [],
      formKey: 0,
      inputDisabled: true,
      orderSettings: null,
      settingWaybillLength: 0,
      selectAllCheckbox: false,
      waybillSeriesList: [],
      prefixList: [],
      matchingSeries: [],
      prefixMatch: [],
      current_loc: '',
      merchantOptions: [],
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
    summaryActualWeight() {
      const value = parseFloat(this.form.weight)
      return Number.isNaN(value) ? '0.00' : value.toFixed(2)
    },
    summaryVolumetricWeight() {
      const value = parseFloat(this.form.volumetric_weight)
      return Number.isNaN(value) ? '0.00' : value.toFixed(2)
    },
    summaryChargeableWeight() {
      const actual = parseFloat(this.form.weight)
      const volumetric = parseFloat(this.form.volumetric_weight)
      const actualWeight = Number.isNaN(actual) ? 0 : actual
      const volumetricWeight = Number.isNaN(volumetric) ? 0 : volumetric
      return Math.max(actualWeight, volumetricWeight).toFixed(2)
    },
  },
  watch: {
    page() {
      this.getOrderList()
    },
    perPage() {
      this.getOrderList()
    },
    isAutoSubmit(newIsAutoSubmit) {
      if (!newIsAutoSubmit) {
        // If isAutoSubmit becomes false, clear the freezeWeight value
        this.freezeWeight = null
      }
    },
    calculateVolumetricWeight(enabled) {
      if (enabled) {
        this.initVolumetricFormFields()
      } else {
        this.clearVolumetricFormFields()
      }
    },
    'form.volumetric_dimension.width': 'handleVolumetricInputChange',
    'form.volumetric_dimension.height': 'handleVolumetricInputChange',
    'form.volumetric_dimension.length': 'handleVolumetricInputChange',
    waybillData: {
      handler(val) {
        let waybillLocated = false
        val.forEach((data, index) => {
          if (data.waybill_number && this.form.waybill_number) {
            if (data.waybill_number.toLowerCase() === this.form.waybill_number.toLowerCase()) {
              waybillLocated = true
              if (!this.form.weight || (this.waybillData.length > 1 && this.waybillData[index - 1] !== {})) {
                this.$set(this.form, 'weight', data.weight || '')
                this.$set(this.form, 'destination_warehouse', data.destination_warehouse.name || '')
                if (this.volumetricSettings.enabled) {
                  this.calculateVolumetricWeight = true
                  this.applyVolumetricFromFetchedOrder(data)
                }
              }
              this.formKey += 1
              this.inputDisabled = false
              this.$nextTick(() => {
                this.$refs.waybill.focus()
              })
            }
          }
        })
        if (!waybillLocated) {
          this.$set(this.form, 'weight', '')
          this.$set(this.form, 'destination_warehouse', '')
          if (this.volumetricSettings.enabled) {
            this.calculateVolumetricWeight = false
            this.clearVolumetricFormFields()
          }
          this.inputDisabled = true
        }
      },
      deep: true,
    },
    // eslint-disable-next-line func-names
    'form.weight': async function (newValue) {
      if (newValue !== '') {
        if ((this.freezeWeight === null || this.freezeWeight === '') && this.form.waybill_number.length >= 9 && this.isAutoSubmit) {
          this.showErrorMessage('Freeze Weight can not be empty')
          this.$set(this.form, 'waybill_number', '')
          this.$set(this.form, 'weight', '')
          return
        }
        if (this.isAutoSubmit && this.form.waybill_number) {
          if (Number(this.freezeWeight) === Number(newValue) && (this.form.waybill_number !== '' || this.form.weight !== '')) {
            await this.onSubmit()
          } else {
            this.showErrorMessage('Order weight not same as freeze weight')
            this.$set(this.form, 'waybill_number', '')
            this.$set(this.form, 'weight', '')
          }
        }
      }
    },
  },
  async mounted() {
    this.totalRows = this.rows.length
    this.inputDisabled = true
    await this.getWaybillSeriesList()
    await this.readOrderSetting()
    await this.readVolumetricSetting()
    this.initVolumetricFormFields()
    this.getMerchantList()
    await this.fetchModules()
  },
  methods: {
    initVolumetricFormFields() {
      if (!this.form.volumetric_dimension) {
        this.$set(this.form, 'volumetric_dimension', { length: null, width: null, height: null })
        this.$set(this.form, 'volumetric_weight', null)
      }
    },
    clearVolumetricFormFields() {
      this.$set(this.form, 'volumetric_weight', null)
      this.$set(this.form, 'volumetric_dimension', { length: null, width: null, height: null })
    },
    parseVolumetricDimension(dim) {
      if (!dim) return null
      if (typeof dim === 'string') {
        try {
          return JSON.parse(dim)
        } catch (e) {
          return null
        }
      }
      return dim
    },
    applyVolumetricFromFetchedOrder(data) {
      this.initVolumetricFormFields()
      const dim = this.parseVolumetricDimension(
        data.volumetric_dimension || data.other_details?.volumetric_dimension,
      )
      if (dim) {
        this.$set(this.form, 'volumetric_dimension', {
          length: dim.length ?? null,
          width: dim.width ?? null,
          height: dim.height ?? null,
        })
      }
      const volumetricWeight = data.volumetric_weight ?? data.other_details?.volumetric_weight
      if (volumetricWeight != null && volumetricWeight !== '') {
        const value = parseFloat(volumetricWeight)
        this.$set(this.form, 'volumetric_weight', Number.isNaN(value) ? null : value.toFixed(2))
      } else {
        const { width, height, length } = this.form.volumetric_dimension || {}
        if (width && height && length) {
          this.getVolumetricWeight(this.form)
        }
      }
    },
    async readVolumetricSetting() {
      try {
        this.volumetricSettings = localStorage.getItem('volumetric_setting') ? JSON.parse(localStorage.getItem('volumetric_setting')) : (await VolumetricSettingsRepository.getVolumetricSetting()).data.data
        if (!localStorage.getItem('volumetric_setting')) localStorage.setItem('volumetric_setting', JSON.stringify(this.volumetricSettings))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    handleVolumetricInputChange() {
      const { width, height, length } = this.form.volumetric_dimension || {}
      if (width && height && length) {
        this.getVolumetricWeight(this.form)
      }
    },
    async getVolumetricWeight(form) {
      try {
        const payload = {
          height: form.volumetric_dimension.height,
          width: form.volumetric_dimension.width,
          length: form.volumetric_dimension.length,
        }
        const res = (await VolumetricSettingsRepository.getVolumetricWeight(payload))
        const value = parseFloat(res.data.data.volumetric_weight)
        this.$set(this.form, 'volumetric_weight', Number.isNaN(value) ? null : value.toFixed(2))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    preventNegative(event) {
      if (event.key === '-') {
        event.preventDefault()
      }
    },
    handlePaste(event) {
      // Access the pasted value from the event
      const pastedText = event.clipboardData.getData('text')
      // Call your function with the pasted value or perform actions as needed
      this.getOrderDetails(pastedText)
    },
    handleKeyDown(event) {
      if (event.key === 'Enter') {
        this.getData()
      }
    },
    getData() {
      this.getOrderDetails(this.form.waybill_number)
    },
    async getOrderDetails(waybill) {
      try {
        this.$set(this, 'current_loc', '')
        const { data } = (await orderRepository.fetchOrderData(waybill))
        this.setBucket(data.next_bucket)
        this.waybillData.push(data.data)
      } catch (e) {
        this.waybillData.push({})
      }
    },
    async setBucket(bucket) {
      if (bucket) {
        this.$set(this, 'current_loc', bucket.name)
        this.bucketColor = bucket.color || 'rgba(115, 103, 240, 0.2)'
      }
    },
    async getOrderList() {
      this.dataLoading = true
      this.rows = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = (await orderRepository.getOrderListByKey(this.status_key, this.page, this.perPage, filterQuery))
        this.rows = data.data
        this.meta = data.meta
        this.submitCount = 0
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    checkWeightRange() {
      const minWeight = this.orderSettings.weight_range_setting.min_weight
      const maxWeight = this.orderSettings.weight_range_setting.max_weight
      const currentWeight = parseFloat(this.form.weight)
      if (this.orderSettings.weight_range_setting.enabled === true && (currentWeight < minWeight || currentWeight > maxWeight)) {
        this.validationState = false
        this.validationMessage = `The order weight should be between ${minWeight} Kg to ${maxWeight} Kg`
        this.isMaxWeight = true
      } else {
        this.validationState = true
        this.validationMessage = ''
        this.isMaxWeight = false
      }
      this.$refs.weightInput.setCustomValidity(this.validationMessage)
    },
    async getOrderListNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await orderRepository.getOrderListByKeyNoPagination(this.status_key, filterQuery))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async readOrderSetting() {
      try {
        this.orderSettings = localStorage.getItem('order_setting') ? JSON.parse(localStorage.getItem('order_setting')) : (await OrderSettingsRepository.getOrderSettingsResource()).data.data
        if (!localStorage.getItem('order_setting')) localStorage.setItem('order_setting', JSON.stringify(this.orderSettings))
        this.settingWaybillLength = this.orderSettings.waybill_setting.auto_waybill_prefix.length + Number(this.orderSettings.waybill_setting.digit_count)
        if (this.orderSettings.waybill_setting.suffix_enabled) this.settingWaybillLength += this.orderSettings.waybill_setting.auto_waybill_suffix.length
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async handleScan(waybillNumber) {
      if (!this.sortingModuleEnabled) {
        return
      }
      try {
        await sortingRepository.scanWaybill({
          waybill_number: waybillNumber,
        })
        // this.$set(this, 'current_loc', '')
      } catch (error) {
        this.convertAndNotifyError(error.response.data.message)
      }
    },
    async onSubmit() {
      this.loadingResponse = true
      try {
        const payload = {
          waybill_numbers: [],
        }
        payload.waybill_numbers.push(this.form.waybill_number)
        payload.status_key = this.status_key
        const hasVolumetricDimensions = this.calculateVolumetricWeight
          && this.form.volumetric_dimension
          && this.form.volumetric_dimension.width
          && this.form.volumetric_dimension.height
          && this.form.volumetric_dimension.length
        payload.other_details = {
          weight: this.form.weight,
          freeze_weight: Number(this.freezeWeight),
          auto_submit: this.isAutoSubmit,
          volumetric_weight: hasVolumetricDimensions ? this.form.volumetric_weight : null,
          volumetric_dimension: hasVolumetricDimensions ? {
            width: parseFloat(this.form.volumetric_dimension.width),
            height: parseFloat(this.form.volumetric_dimension.height),
            length: parseFloat(this.form.volumetric_dimension.length),
          } : null,
        }

        await orderRepository.updateOrderStatus(payload)
        this.handleScan(this.form.waybill_number)
        this.waybillData = []
        this.showSuccessMessage('Order updated successfully')
        this.submitCount += 1
        this.clearFormData()
      } catch (e) {
        this.convertAndNotifyError(e)
        this.clearFormData()
      }
    },
    clearFormData() {
      this.$set(this.form, 'waybill_number', '')
      this.$set(this.form, 'destination_warehouse', '')
      this.$set(this.form, 'weight', '')
      this.calculateVolumetricWeight = false
      this.clearVolumetricFormFields()
      this.loadingResponse = false
      this.$nextTick(() => {
        this.$refs.waybill.focus()
      })
    },
    async getMerchantList() {
      this.merchantOptions = localStorage.getItem('merchant_list') ? JSON.parse(localStorage.getItem('merchant_list')) : (await merchantRepository.getMerchantListForDropdown()).data.data
      if (!localStorage.getItem('merchant_list')) localStorage.setItem('merchant_list', JSON.stringify(this.merchantOptions))
    },
    onClickViewOrderModal(id) {
      this.$refs.viewOrderModal.openModal(id)
    },
    onClickBulkAction() {
      this.$refs.bulkActionModal.openModal()
    },
    onClickSelectAll() {
      this.selectedWaybillArray = this.rows.map(n => n.waybill_number)
    },
    onClickDeselctAll() {
      this.selectedWaybillArray = []
    },
    filterQueryUpdate() {
      this.getOrderList()
      this.onClickDeselctAll()
    },
    handleChangePage(page) {
      this.page = page
      this.getOrderList()
    },
    changePage(value) {
      this.perPage = value
    },
    sortChanged(e) {
      const key = e.sortBy
      if (key) {
        const active = e.sortDesc ? '-' : ''
        this.sort = active + key
        this.fetchOrderList()
      }
    },
    onClickRefresh() {
      this.getOrderList()
    },
    onClickSelectAllCheckbox() {
      if (this.selectAllCheckbox) {
        // If the "Select All" checkbox is checked, select all waybill numbers.
        this.selectedWaybillArray = this.rows.map(n => n.waybill_number)
      } else {
        this.selectedWaybillArray = []
      }
    },
    async fetchModules() {
      try {
        this.loading = true
        const data = localStorage.getItem('module_data') ? JSON.parse(localStorage.getItem('module_data')) : (await ResourceRepository.getModules()).data.data
        if (!localStorage.getItem('module_data')) localStorage.setItem('module_data', JSON.stringify(data))
        const sortingSettingModule = data.find(module => module.name === 'Sorting Module')
        this.sortingModuleEnabled = sortingSettingModule ? sortingSettingModule.is_active : false
      } catch (e) {
        // this.convertAndNotifyError(e)
      }
      this.loading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.scan-form__top-row,
.scan-form__volumetric-row,
.scan-form__submit-row {
  margin-left: 0;
  margin-right: 0;
}

.scan-form__left-col {
  padding-left: 0;
  padding-right: 0;

  @media (min-width: 768px) {
    padding-right: 0.75rem;
  }

  > .b-row {
    margin-left: 0;
    margin-right: 0;
  }
}

.scan-form__volumetric-col {
  padding-left: 0;
  padding-right: 0;
}

.scan-form__fields-row,
.scan-form__checkbox-row {
  margin-left: 0;
  margin-right: 0;

  > [class*='col-']:first-child {
    padding-left: 0;
  }
}

.scan-form__fields-row {
  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
}

.scan-form__checkbox-row {
  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
}

.scan-form__waybill-col {
  @media (min-width: 768px) {
    flex: 1.4 1 0;
    max-width: none;
  }
}

.scan-form__search-col {
  @media (min-width: 768px) {
    flex: 0 0 2.75rem;
    max-width: 2.75rem;
    padding-left: 0.35rem;
    padding-right: 0.35rem;
  }
}

.scan-form__metric-col {
  @media (min-width: 768px) {
    flex: 1.15 1 0;
    max-width: none;
  }
}

.scan-form__dest-col {
  @media (min-width: 768px) {
    flex: 1.6 1 0;
    max-width: none;
    padding-right: 0.25rem;
  }
}

.scan-form__right-col {
  margin-top: 0.75rem;

  @media (min-width: 768px) {
    margin-top: 0;
    padding-left: 0.5rem;
  }
}

.scan-form__submit-col {
  display: flex;
  justify-content: flex-end;
}

.scanned-order {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-weight: 700;
  color: white !important;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.35);
  background-color: var(--bucket-bg-color) !important;
  border-radius: 0.428rem;
  text-align: center;

  &--panel {
    min-height: 5.25rem;
    font-size: 2.75rem;
    line-height: 1;
    padding: 1.25rem 1rem;
  }

  &--empty {
    opacity: 0.7;
  }
}

.volumetric-field-col {
  flex: 1 1 0;
  min-width: 0;
  max-width: none;
  padding-left: 0.35rem;
  padding-right: 0.35rem;

  &:first-child {
    padding-left: 0;
  }

  &--dimension {
    flex: 1.35 1 0;
  }

  &--weight {
    flex: 1.05 1 0;
  }
}

.volumetric-section {
  padding: 1rem 1.25rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  background: #fff;
}

.volumetric-section__title {
  margin-bottom: 0.75rem;
  color: #b9b9c3;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.volumetric-section__body {
  margin-left: 0;
  margin-right: 0;

  > [class*='col-'] {
    padding-top: 0;
  }
}

.volumetric-section__dimensions {
  .volumetric-input-group ::v-deep .form-control {
    min-height: 2.85rem;
    font-size: 1rem;
  }

  .volumetric-input-group ::v-deep .input-group-text {
    min-height: 2.85rem;
    font-size: 0.95rem;
  }
}

.volumetric-section__summary {
  align-self: flex-start;
  margin-top: 0;
  padding-right: 0;

  @media (min-width: 768px) {
    padding-top: 0;
  }
}

@media (max-width: 767.98px) {
  .volumetric-section__summary {
    margin-top: 1rem;
  }
}

.volumetric-section__labels-row,
.volumetric-section__equation-row {
  margin-left: 0;
  margin-right: 0;
  width: 100%;
}

.volumetric-input-group {
  width: 100%;

  ::v-deep .form-control {
    min-width: 0;
  }

  ::v-deep .input-group-text {
    background: #fff;
    color: #5e5873;
    font-size: 0.857rem;
    font-weight: 700;
  }
}

.volumetric-field-label {
  display: block;
  margin-bottom: 0.35rem;
  color: #6e6b7b;
  font-size: 0.857rem;
}

.volumetric-separator-spacer {
  flex: 0 0 auto;
  width: 1.75rem;
  padding-left: 0.35rem;
  padding-right: 0.35rem;
}

.volumetric-separator-col {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 1.75rem;
  padding-left: 0.35rem;
  padding-right: 0.35rem;
}

.volumetric-separator {
  color: #b9b9c3;
  font-size: 1rem;
  font-weight: 500;
  line-height: 2.85rem;
}

.volumetric-input-group--weight {
  ::v-deep .volumetric-weight-input {
    text-overflow: clip;
    color: #4186f5;
    font-weight: 600;
  }
}

.volumetric-weight-append {
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
}

.volumetric-weight-badge {
  display: inline-flex;
  align-items: center;
  height: 100%;
  padding: 0 0.75rem;
  border: 1px solid #d4effa;
  border-left: none;
  border-radius: 0 0.357rem 0.357rem 0;
  background: #eff9ff;
  color: #4186f5;
  font-size: 0.857rem;
  font-weight: 600;
  white-space: nowrap;
}

.weight-summary {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding: 1rem 1.25rem;
  border: 1px solid #d4effa;
  border-radius: 0.428rem;
  background: #eff9ff;

  &--in-card {
    justify-content: flex-start;
    padding: 0.85rem 1rem;

    @media (min-width: 768px) {
      max-width: 100%;
      width: 100%;
    }
  }
}

.weight-summary__title {
  margin-bottom: 0.5rem;
  color: #b9b9c3;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.weight-summary__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.35rem;
}

.weight-summary__label {
  color: #5e5873;
  font-size: 0.8rem;
  font-weight: 400;
}

.weight-summary__value {
  color: #5e5873;
  font-size: 0.8rem;
  font-weight: 700;
}

.weight-summary__divider {
  margin: 0.4rem 0;
  border-top: 1px solid #ebe9f1;
}

.weight-summary__row--chargeable {
  margin-bottom: 0;
}

.weight-summary__value--chargeable {
  color: #4186f5;
  font-size: 0.875rem;
  font-weight: 700;
}
</style>
